/**
 * source.ts — «profilo sorgente FIPAV» dell'istanza.
 *
 * PERCHÉ ESISTE QUESTO MODULO
 * ---------------------------
 * Il portale nasce per la Lega Puglia, ma è pensato per girare come **una istanza
 * per comitato**: ogni comitato regionale FIPAV (Puglia, Veneto, Emilia-Romagna, …)
 * pubblica i propri dati su un portale costruito sullo **stesso CMS
 * (Newb/PowerSPORT)**, con le stesse rotte logiche ma URL di ingresso diverse.
 *
 * Perché «una istanza per comitato» e non un'unica istanza multi-comitato? Perché i
 * dati che importiamo (gare/classifiche, elenco società, loghi) sono **per comitato**:
 * un solo comitato per istanza evita di mescolare classifiche e registri di comitati
 * diversi, tiene ogni deployment autonomo e non costringe a filtrare per ComitatoId
 * ovunque (il CMS di Veneto ed Emilia-Romagna accetta e **ignora** ComitatoId).
 *
 * Quindi i valori che fino a ieri erano cablati su `www.fipavpuglia.it` in una decina
 * di file vivono qui, in un profilo per istanza; il resto del codice li legge da qui.
 *
 * SELEZIONE E OVERRIDE
 * --------------------
 *  - `FIPAV_SOURCE` = `puglia` (default) | `veneto` | `emilia-romagna`.
 *    Assente o sconosciuto → profilo `puglia`, senza throw e senza log: un refuso
 *    nell'env non deve far esplodere (né sporcare i log di) un'istanza in produzione.
 *  - Ogni campo è sovrascrivibile singolarmente via env — `FIPAV_ORIGIN`,
 *    `FIPAV_GARE_PATH`, `FIPAV_GARE_QUERY`, `FIPAV_ELENCO_PATH`, `FIPAV_SOCIETA_PID`,
 *    `FIPAV_COMITATO_ID` (più `FIPAV_SOURCE_LABEL`) — per un comitato nuovo che non
 *    vogliamo ancora codificare come profilo.
 *
 * INVARIANTE (non negoziabile)
 * ----------------------------
 * Con la configurazione di default (`FIPAV_SOURCE` assente) i valori restituiti sono
 * **identici carattere per carattere** alle costanti cablate di prima
 * (`https://www.fipavpuglia.it/gare`, `…/elenco-societa.aspx?PId=174`, ComitatoId 9):
 * le URL generate non cambiano di una virgola. Le funzioni di composizione URL qui
 * sotto esistono proprio per rendere questo verificabile in un solo punto.
 *
 * Il modulo è **puro** (nessun I/O, legge solo l'ambiente): può essere importato
 * anche da codice che finisce nel bundle client, dove le env non-`NEXT_PUBLIC_`
 * valgono `undefined` → profilo `puglia` (vedi la nota su `FIPAV_SOURCE_ORIGIN_HOSTS`).
 *
 * Dati verificati il 2026-10-16 (tutte le rotte rispondono 200, stesso CMS):
 *  - gare:         Puglia `/gare` · Veneto `/calendari-gare` (stessi filtri + GaraGiornata)
 *                  · ER `/risultati-classifiche.aspx?PId=29422`
 *  - elenco soc.:  Puglia/ER `/elenco-societa.aspx?PId=<pid>` · Veneto `/societa`
 *  - classifica girone: `/classifica.aspx?CId=<id>` (tutti)
 *  - loghi:        `/mngArea/Societa/img/<SId>/Loghi/LogoB<SId>.<ext>` (tutti)
 */

export type FipavSourceId = 'puglia' | 'veneto' | 'emilia-romagna'

export interface FipavSourceProfile {
  id: FipavSourceId
  /** Etichetta leggibile del comitato (solo diagnostica). */
  label: string
  /** Origine (schema + host) del sito del comitato: ci si appendono i path. */
  origin: string
  /** Rotta delle gare/calendario, senza query. */
  garePath: string
  /** Query fissa della rotta gare (ER: `PId=29422`); vuota quando la rotta basta. */
  gareBaseQuery: string
  /** Rotta dell'elenco società. */
  elencoSocietaPath: string
  /** PId dell'elenco società (Puglia/ER); `null` per i siti che non lo usano (Veneto). */
  societaPid: number | null
  /** ComitatoId da passare nelle query FIPAV (Veneto/ER lo accettano e lo ignorano). */
  comitatoId: number
  /**
   * Comitati territoriali da scansionare per popolare il registro società
   * (`syncSocietaRegistry`): sono le voci del dropdown «ComitatoId» della pagina
   * `elenco-societa` **di questo comitato**.
   *
   * Puglia = [10, 14, 11] (Bari-Foggia, Lecce, Taranto): è la lista storicamente
   * cablata, non va toccata. Veneto/ER sono state trascritte dal loro dropdown
   * (verificate 2026-10-16).
   *
   * Lista vuota = su questa istanza il registro società non viene popolato: meglio
   * niente che scandire comitati di un'altra regione.
   */
  comitatiIds: number[]
  /**
   * Segmento **centrale** del codice federale (`<CR>.<provincia>.<progressivo>`,
   * es. `15.073.0123`) → sigla della provincia dell'elenco società.
   *
   * Puglia = 073 BA, 074 BR, 075 FG, 076 LE, 077 TA (cablate, invariate).
   * Veneto = 023 BL, 024 PD, 025 RO, 026 TV, 027 VE, 028 VR, 029 VI.
   * Emilia-Romagna = vuoto: vedi il commento sul profilo (il suo `elenco-societa`
   * ignora `ComitatoId`, quindi non è enumerabile con una richiesta per comitato
   * e il codice 034 è l'unico verificato finora — Bologna).
   *
   * Non dedurre i codici da una regola: sono progressivi assegnati dal CMS e
   * vanno letti (o verificati città per città) sul comitato giusto.
   */
  provinceCodes: Record<string, string>
  /**
   * Stagioni da seminare in `hub_seasons` al primo avvio, quando la tabella è
   * vuota. Servono perché l'istanza abbia subito una stagione **attiva**: senza,
   * il portale parte e non pubblica nulla.
   *
   * Gli id stagione **non sono globali**: sono progressivi del CMS e cambiano da
   * comitato a comitato (verificato 2026-10-16: il 2025/26 è 2271 in Puglia, 2263
   * in Veneto, 2297 in Emilia-Romagna). Seminare gli id pugliesi su un'istanza
   * Veneto significa creare una stagione attiva che non contiene nessun
   * campionato → portale vuoto senza errori.
   */
  knownSeasons: { label: string; stagioneId: string; isActive: boolean }[]
  /**
   * Data della **prima partita** dei campionati maschili di Serie C/D della
   * stagione in corso (ISO con offset), usata dal countdown di pre-season in home.
   *
   * PERCHÉ sta nel profilo: è una data **del comitato**, non nazionale. Prima era
   * una costante unica (`2026-10-17`), quindi ogni portale contava i giorni verso
   * l'inizio del campionato **pugliese**: su veneto.pallavolo.me diceva «sabato 17
   * ottobre» mentre il Veneto inizia il 27, e sull'Emilia-Romagna addirittura il 15
   * (già passato), dove il countdown non doveva più comparire.
   *
   * Va aggiornata **una volta a stagione**, leggendo il calendario del comitato
   * (basta la prima data che compare nelle gare dei gironi C/D maschili). Il
   * mezzogiorno evita che il rendering in UTC mostri il giorno prima.
   */
  menSeriesFirstMatchIso: string
}

/**
 * I profili noti. I valori `puglia` sono quelli **storicamente cablati** nel codice:
 * non vanno toccati o l'invariante di default salta.
 *
 * Nota su `comitatoId` di Veneto ed ER: il loro CMS accetta il parametro e lo ignora
 * (verificato 2026-10-16), quindi resta il valore neutro 9 — è inerte nella query ed
 * è sovrascrivibile con `FIPAV_COMITATO_ID` se un giorno il CMS lo userà davvero.
 */
export const FIPAV_SOURCE_PROFILES: Record<FipavSourceId, FipavSourceProfile> = {
  puglia: {
    id: 'puglia',
    label: 'CR FIPAV Puglia',
    origin: 'https://www.fipavpuglia.it',
    garePath: '/gare',
    gareBaseQuery: '',
    elencoSocietaPath: '/elenco-societa.aspx',
    societaPid: 174,
    comitatoId: 9,
    comitatiIds: [10, 14, 11],
    provinceCodes: {
      '073': 'BA', // Bari (copre anche BT)
      '074': 'BR', // Brindisi
      '075': 'FG', // Foggia
      '076': 'LE', // Lecce
      '077': 'TA', // Taranto
    },
    // Id verificati il 2026-10-16 sul suo `/gare?act=campionato`.
    knownSeasons: [
      { label: '2025/26', stagioneId: '2271', isActive: true },
      { label: '2026/27', stagioneId: '2389', isActive: false },
      { label: '2024/25', stagioneId: '2152', isActive: false },
    ],
    menSeriesFirstMatchIso: '2026-10-17T12:00:00+02:00',
  },
  veneto: {
    id: 'veneto',
    label: 'CR FIPAV Veneto',
    origin: 'https://www.fipavveneto.net',
    garePath: '/calendari-gare',
    gareBaseQuery: '',
    elencoSocietaPath: '/societa',
    societaPid: null,
    comitatoId: 9,
    // Dropdown «ComitatoId» di https://www.fipavveneto.net/societa (2026-10-16):
    // 3 Padova · 4 Rovigo · 5 Treviso-Belluno · 6 Venezia · 7 Verona · 8 Vicenza.
    comitatiIds: [3, 4, 5, 6, 7, 8],
    // Codici federali Veneto (`06.<provincia>.<progressivo>`), verificati il
    // 2026-10-16 leggendo le città delle società di ogni comitato:
    //   023 -> Belluno, 024 -> Padova, 025 -> Rovigo, 026 -> Treviso,
    //   027 -> Venezia, 028 -> Verona, 029 -> Vicenza.
    provinceCodes: {
      '023': 'BL', // Belluno
      '024': 'PD', // Padova
      '025': 'RO', // Rovigo
      '026': 'TV', // Treviso
      '027': 'VE', // Venezia
      '028': 'VR', // Verona
      '029': 'VI', // Vicenza
    },
    // Id verificati il 2026-10-16 sul suo `/calendari-gare?act=campionato`.
    knownSeasons: [
      { label: '2025/26', stagioneId: '2263', isActive: true },
      { label: '2026/27', stagioneId: '2381', isActive: false },
      { label: '2024/25', stagioneId: '2144', isActive: false },
    ],
    // Prima partita C/D maschile 2026/27: Serie D il 27/10, Serie C il 31/10
    // (letto dai gironi sul portale del comitato, non dedotto).
    menSeriesFirstMatchIso: '2026-10-27T12:00:00+01:00',
  },
  'emilia-romagna': {
    id: 'emilia-romagna',
    label: 'CR FIPAV Emilia-Romagna',
    origin: 'https://crer.portalefipav.net',
    garePath: '/risultati-classifiche.aspx',
    gareBaseQuery: 'PId=29422',
    elencoSocietaPath: '/elenco-societa.aspx',
    societaPid: 29425,
    comitatoId: 9,
    // Dropdown «ComitatoId» di
    // https://crer.portalefipav.net/elenco-societa.aspx?PId=29425 (2026-10-16):
    // 70 Bologna · 71 Ferrara · 74 Piacenza · 36 Ravenna · 109 Reggio Emilia · 75 Romagna.
    comitatiIds: [70, 71, 74, 36, 109, 75],
    // MANCA la mappa codice → provincia. Non la scrivo a naso: il codice è un
    // progressivo del CMS (per la Puglia 073..077 sono assegnati in ordine
    // alfabetico di nome provincia, ma **non** è una regola di cui fidarsi).
    // Verificato finora un solo valore (034 = Bologna, prima pagina dell'elenco).
    // Per completarla: aprire `elenco-societa.aspx?PId=29425`, sfogliare le
    // pagine (`&Pagina=N`) raccogliendo `<CR>.<codice>.<progressivo>` + località
    // di ogni società e mappare ogni codice sulla provincia delle sue città.
    // Nota: su questo comitato `ComitatoId` è **ignorato** dall'elenco, quindi non
    // si può enumerare una provincia per volta come si fa in Veneto.
    provinceCodes: {},
    // Id verificati il 2026-10-16 sul suo
    // `/risultati-classifiche.aspx?PId=29422&act=campionato`.
    knownSeasons: [
      { label: '2025/26', stagioneId: '2297', isActive: true },
      { label: '2026/27', stagioneId: '2415', isActive: false },
      { label: '2024/25', stagioneId: '2178', isActive: false },
    ],
    // Prima partita C/D maschile 2026/27: 15/10 (Serie D maschile il 15, Serie C il 16),
    // letto dai gironi sul portale del comitato.
    menSeriesFirstMatchIso: '2026-10-15T12:00:00+02:00',
  },
}

const DEFAULT_SOURCE_ID: FipavSourceId = 'puglia'

/** Id sconosciuto o assente → Puglia: nessun throw, nessun log. */
function normalizeSourceId(value: string | undefined): FipavSourceId {
  const key = (value ?? '').trim().toLowerCase()
  return key in FIPAV_SOURCE_PROFILES ? (key as FipavSourceId) : DEFAULT_SOURCE_ID
}

/** Env stringa: `undefined` quando assente o vuota (una env vuota non deve svuotare l'URL). */
function envString(name: string): string | undefined {
  const raw = process.env[name]
  if (raw == null) return undefined
  const trimmed = raw.trim()
  return trimmed === '' ? undefined : trimmed
}

/** Env numerica: `undefined` = non impostata, `null` = impostata vuota (nessun valore). */
function envInt(name: string): number | null | undefined {
  const raw = process.env[name]
  if (raw == null) return undefined
  const trimmed = raw.trim()
  if (trimmed === '') return null
  const parsed = Number.parseInt(trimmed, 10)
  return Number.isFinite(parsed) ? parsed : undefined
}

/** Path sempre con `/` iniziale e senza `/` finale (l'origine è già senza slash). */
function normalizePath(value: string): string {
  const withLeadingSlash = value.startsWith('/') ? value : `/${value}`
  return withLeadingSlash.replace(/\/+$/, '') || '/'
}

function resolveFipavSource(): FipavSourceProfile {
  const id = normalizeSourceId(process.env.FIPAV_SOURCE)
  const base = FIPAV_SOURCE_PROFILES[id]

  const societaPidOverride = envInt('FIPAV_SOCIETA_PID')
  const comitatoIdOverride = envInt('FIPAV_COMITATO_ID')

  return {
    id,
    label: envString('FIPAV_SOURCE_LABEL') ?? base.label,
    origin: (envString('FIPAV_ORIGIN') ?? base.origin).replace(/\/+$/, ''),
    garePath: normalizePath(envString('FIPAV_GARE_PATH') ?? base.garePath),
    gareBaseQuery: (envString('FIPAV_GARE_QUERY') ?? base.gareBaseQuery).replace(/^\?/, ''),
    elencoSocietaPath: normalizePath(envString('FIPAV_ELENCO_PATH') ?? base.elencoSocietaPath),
    societaPid: societaPidOverride === undefined ? base.societaPid : societaPidOverride,
    // `null` (env vuota) non è un ComitatoId valido: in quel caso si tiene il default
    // del profilo, perché la colonna `fipav_championships.comitato_id` è NOT NULL.
    comitatoId: comitatoIdOverride == null ? base.comitatoId : comitatoIdOverride,
    // Liste del profilo (non sovrascrivibili da env: descrivono *quali* comitati
    // territoriali e *quali* province ha quel comitato). Copie: chi le legge non
    // deve poter mutare la tabella dei profili, che è condivisa e cachata.
    comitatiIds: [...base.comitatiIds],
    provinceCodes: { ...base.provinceCodes },
    // Stagioni da seminare al primo avvio: sono del comitato, non sovrascrivibili
    // da env (cambiarle a mano significherebbe accoppiare l'istanza alla stagione
    // di un altro comitato). Copia, come le liste qui sopra.
    knownSeasons: base.knownSeasons.map((s) => ({ ...s })),
    // La data del countdown è del comitato: si può sovrascrivere da env per una
    // correzione d'emergenza senza ricompilare (es. `FIPAV_FIRST_MATCH_ISO`).
    menSeriesFirstMatchIso: envString('FIPAV_FIRST_MATCH_ISO') ?? base.menSeriesFirstMatchIso,
  }
}

let cachedProfile: FipavSourceProfile | null = null

/** Profilo sorgente dell'istanza (risolto una volta sola: l'env non cambia a runtime). */
export function fipavSource(): FipavSourceProfile {
  if (!cachedProfile) cachedProfile = resolveFipavSource()
  return cachedProfile
}

/** Base della rotta gare, con l'eventuale query fissa del CMS (ER: `?PId=29422`). */
export function fipavGareBaseUrl(): string {
  const { origin, garePath, gareBaseQuery } = fipavSource()
  return gareBaseQuery ? `${origin}${garePath}?${gareBaseQuery}` : `${origin}${garePath}`
}

/**
 * Compone una query sulla rotta gare scegliendo il separatore giusto: `?` quando la
 * base non ha già una query (Puglia/Veneto), `&` quando ce l'ha (ER, che porta `PId=`).
 */
export function fipavGareUrl(query: string): string {
  const base = fipavGareBaseUrl()
  return `${base}${base.includes('?') ? '&' : '?'}${query}`
}

/**
 * URL dell'elenco società. Il profilo esprime **entrambe** le forme in uso:
 *  - Puglia/ER: `/elenco-societa.aspx?PId=<pid>&…` (il PId seleziona l'elenco);
 *  - Veneto:    `/societa?…` (nessun PId).
 */
export function fipavElencoSocietaUrl(query = ''): string {
  const { origin, elencoSocietaPath, societaPid } = fipavSource()
  const params = [societaPid != null ? `PId=${societaPid}` : null, query]
    .filter((param): param is string => Boolean(param))
  const base = `${origin}${elencoSocietaPath}`
  return params.length ? `${base}?${params.join('&')}` : base
}

/**
 * Host delle origini dei profili noti (senza schema), per l'allowlist del proxy loghi.
 *
 * PERCHÉ serve l'elenco **completo** e non solo l'origine dell'istanza:
 * `src/lib/logo/proxy-url.ts` è importato anche da **client component** (scorebug
 * live, hero del club, …), dove le env non-`NEXT_PUBLIC_` sono `undefined`. Un'istanza
 * Veneto/ER costruirebbe lato client un'allowlist ridotta al solo host pugliese e
 * smetterebbe di proxare i propri loghi (src non consentita → 403 / immagine rotta).
 * Elencare le origini note tiene l'allowlist identica su server e client; per
 * un'istanza con `FIPAV_ORIGIN` custom resta comunque il caso server-side (vedi
 * `LOGO_PROXY_ALLOWED_HOSTS`, che aggiunge anche l'origine risolta).
 */
export const FIPAV_SOURCE_ORIGIN_HOSTS: string[] = Array.from(
  new Set(Object.values(FIPAV_SOURCE_PROFILES).map((profile) => new URL(profile.origin).hostname)),
)
